import { __awaiter } from "tslib";
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { InsuranceType } from '@websky/graphql';
import { Banner } from '../Banner/Banner';
import { Card } from '../Card/Card';
import { useMergeClasses, useTheme } from '../../../theme';
import IconButton from '../../../BaseComponents/Button/components/IconButton/IconButton';
import { InsurancePrice } from '../InsurancePrice/InsurancePrice';
import { SelectForm } from '../SelectForm/SelectForm';
import Link from '../../../Link';
import { OptionsList } from '../OptionsList/OptionsList';
import { isExistI18nTranslate } from '../../../utils';
import { CdnIcon } from '@shared/ui';
export const InsuranceProductView = ({ insuranceProgram, className, classes, popular, policyUrl, onSelect, onRemove, onDownloadVskInsurance, hideForAllPassengersTextBecauseSinglePassenger, isShowImportantInsurancesError, bannerVariant = 'icon', popularPricePosition = 'withinRadio', showIconButton, isSelected, isConfirmed, isTouched, isError, handleToggle }) => {
    var _a;
    const { t } = useTranslation('InsurancePage');
    const { InsuranceProduct: defaultTheme } = useTheme('InsurancePage');
    const theme = useMergeClasses(defaultTheme, classes);
    const [isLoading, setIsLoading] = useState(false);
    const onDownloadVskInsuranceHandler = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        setIsLoading(true);
        yield (onDownloadVskInsurance === null || onDownloadVskInsurance === void 0 ? void 0 : onDownloadVskInsurance());
        setIsLoading(false);
    }), [onDownloadVskInsurance]);
    const infoLink = useMemo(() => {
        const isVskInsurance = insuranceProgram.type === InsuranceType.Vsk;
        const isConfirmedAndCanDownload = isConfirmed && (policyUrl || isVskInsurance);
        if (!insuranceProgram.urlToOffer && !isConfirmedAndCanDownload) {
            return null;
        }
        return (React.createElement("div", { className: theme.linksWrapper },
            isExistI18nTranslate('Offer', { ns: 'InsurancePage' }) ? (React.createElement("div", { className: theme.moreInfo },
                React.createElement(CdnIcon, { icon: "Info/Details", className: theme.infoIcon }),
                React.createElement("span", null,
                    t('More details in'),
                    "\u00A0"),
                React.createElement(Link, { className: theme.moreInfo, action: insuranceProgram.urlToOffer, target: "_blank" }, t('Offer')))) : (React.createElement(Link, { className: theme.moreInfo, action: insuranceProgram.urlToOffer, target: '_blank' },
                React.createElement(CdnIcon, { icon: "Info/Details", className: theme.infoIcon }),
                t('More details'))),
            isConfirmedAndCanDownload && (React.createElement(Link, { className: cn(theme.moreInfo, { [theme.moreInfo_loading]: isLoading }), action: isVskInsurance ? onDownloadVskInsuranceHandler : policyUrl, target: !isVskInsurance ? '_blank' : undefined },
                React.createElement(CdnIcon, { icon: "Action/Download", className: theme.infoIcon }),
                t('Download police')))));
    }, [
        insuranceProgram.urlToOffer,
        insuranceProgram.type,
        policyUrl,
        isConfirmed,
        onDownloadVskInsuranceHandler,
        isLoading
    ]);
    return (React.createElement(Card, { className: cn(theme.card, theme.border, theme.shadow, {
            [theme.popular]: popular,
            [theme.confirmed]: isConfirmed
        }, className) },
        React.createElement(Banner, { className: theme.banner, popular: popular, title: insuranceProgram.name, code: insuranceProgram.code, type: insuranceProgram.type, variant: bannerVariant }),
        React.createElement("div", { className: theme.content },
            showIconButton && (React.createElement(IconButton, { onClick: handleToggle, isSuccess: isSelected || isConfirmed, className: cn(theme.selectButton, {
                    [theme.confirmed]: isConfirmed
                }) },
                React.createElement(CdnIcon, { icon: "Action/Add" }))),
            React.createElement("div", { className: theme.contentWrapper },
                bannerVariant === 'icon' && React.createElement("div", { className: theme.title }, insuranceProgram.name),
                insuranceProgram.description && (React.createElement("div", { className: theme.description, dangerouslySetInnerHTML: { __html: insuranceProgram.description } })),
                !!((_a = insuranceProgram.options) === null || _a === void 0 ? void 0 : _a.length) && (React.createElement(OptionsList, { className: theme.options, options: insuranceProgram.options.map(({ name }) => name) })))),
        React.createElement("div", { className: theme.footer },
            infoLink,
            popular && !isConfirmed && (React.createElement(SelectForm, { className: theme.formControl, onSelect: () => onSelect(insuranceProgram.code), onRemove: () => onRemove(insuranceProgram.code), selected: isSelected, hideForAllPassengersTextBecauseSinglePassenger: hideForAllPassengersTextBecauseSinglePassenger, isTouched: isTouched, isError: isError && isShowImportantInsurancesError, price: popularPricePosition === 'withinRadio' ? insuranceProgram.totalPrice : undefined })),
            popular && popularPricePosition === 'bottom' && (React.createElement(InsurancePrice, { selected: isSelected, price: insuranceProgram.totalPrice, variant: "bottomPrice", hideForAllPassengersTextBecauseSinglePassenger: false })),
            !popular && (React.createElement(InsurancePrice, { className: theme.footerPrice, selected: isSelected, price: insuranceProgram.totalPrice, variant: "regular", onClick: !isConfirmed ? handleToggle : undefined, onRemove: handleToggle, hideForAllPassengersTextBecauseSinglePassenger: hideForAllPassengersTextBecauseSinglePassenger })))));
};
